import {Header, Layout} from '@components/common'
import {fetchJson, getShopNavigation, redirectTo404Page} from "../index";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import s from "./races.module.scss";
import Link from "next/link";
import Image from "next/image";

export interface Nav {
  id: number;
  name: string;
  seq_nr: number;
  type: number;
}

export interface Entry {
  race: number;
  rnk: number;
  type: string;
  date_start: string;
  date_end: string;
  medalIco: string;
  final_gc_id: string;
  finished: string;
  name: string;
  flag: any;
  id: number;
  rider: string;
  nation: string;
  team: string;
  timelag: string;
}

export interface Result {
  id: number;
  stage_type: number; // 4 is overall classification!
  entries: Entry[];
}

export interface Results {
  result: Result[];
}

export interface Stage {
  "id": number,
  "race_date": string;
  "stage_type": number,
  "stagename": string;
  "city_start": string;
  "city_finish": string;
  "distance": number;
  "results"?: Entry[];
}

export interface Race {
  event_id: number;
  profile_img: string;
  name: string;
  stage: string;
  race_date: string;
  tab_id: number;
  startlist_id: number;
  distance: number;
  category: string;
  category_id: number;
  city_start: string;
  city_finish: string;
  nav: Nav[];
  id: number;
  nation: string;
  results: Results;
  gc_result: Entry[];
  stage_result: Entry[];
  stages: Stage[]
}

export interface Month {
  nr: number;
  monthname: string;
  entries: Entry[];
}

export function getRace(id: number): any {
  return new Promise(async (resolve) => {
    const proCyclingStatsApiKey = 'c4ba56cc03e6f9fe1019edecdafe8c1168c986db';
    const race = await fetchJson(
      `https://www.procyclingstats.com/dataservice.php?key=${proCyclingStatsApiKey}&code=api-boh-results&id=${id}`,
      null
    );
    resolve(race);
  });
}

export function getRaces(): any {
  return new Promise(async (resolve) => {
    const proCyclingStatsApiKey = 'c4ba56cc03e6f9fe1019edecdafe8c1168c986db';
    const year = new Date().getFullYear();
    const races = await fetchJson(
      `https://www.procyclingstats.com/dataservice.php?key=${proCyclingStatsApiKey}&code=api-boh-race-ids&season=${year}&monthview=1`,
      'data'
    );
    resolve(races);
  });
}

function parseUpcomingRaces(month: Month, maxNrOfRacesToReturn: number = 3): Promise<Entry[]> {
  return new Promise(async (resolve) => {
    const currentDate = new Date();
    let response: Entry[] = [];
    if (month.nr >= currentDate.getMonth() + 1 && month.entries) {
      month.entries.forEach(entry => {
        if (response.length >= maxNrOfRacesToReturn) {
          return;
        }
        const date_start = new Date(entry.date_start);
        if (currentDate < date_start) {
          /*entry.flag = getFlagEmoji(entry.nation);*/
          response.push(entry);
        }
      });
    }
    resolve(response);
  });
}

export async function getServerSideProps(context: any) {
  try {
    const {locale, params} = context;
    const id = params.id[0];
    const race: Race = await getRace(id);
    if (race && race.stages) {
      const promises = race.stages.map(stage => {
        return new Promise(async (resolve) => {
          const data: Race = await getRace(stage.id);
          stage.results = data.stage_result;
          resolve(data.stage_result);
        })
      });
      await Promise.all(promises);
    }
    const races: any = await getRaces();
    let upcomingRaces: Entry[] = [];
    if (races && races.length > 0) {
      for (let i = 0; i < races.length && upcomingRaces.length < 3; i++) {
        upcomingRaces = [...upcomingRaces, ...await parseUpcomingRaces(races[i], 3 - upcomingRaces.length)];
      }
    }
    return {
      props: {
        ...(await serverSideTranslations(locale, ["common", "home"])),
        ...(await getShopNavigation(locale)), // makes "ShopNavigationColumn" property available on page props
        race,
        upcomingRaces,
        locale
      }
    };

  } catch (error) {
    console.error(error)
    return redirectTo404Page();
  }
}

export default function Race(
  data: any
) {
  const race: Race = data.race;
  const upcomingRaces: Entry[] = data.upcomingRaces;
  return (
    <>
      <div className="container-rg">
        <Header></Header>
        <div className={s.raceDetail}>
          <header className={s.header}>
            <span>
               <Image
                 src={"/assets/flags/" + race.nation?.toUpperCase() + ".svg"}
                 alt="Flag"
                 width={30}
                 height={30}
                 priority={false}
                 quality="100"
               />
            </span>
            <div>
              <h1>{race.name}</h1>
              <p className="text-accent-4">
                {
                  new Date(race.race_date).toLocaleDateString('de-de',
                    {year: "numeric", month: "long", day: "numeric"})
                }
              </p>
            </div>
          </header>
          <div className={s.body}>
            <main>
              {
                race.profile_img &&
                <Image
                  className="mb-6"
                  src={race.profile_img!}
                  alt={'Race results'}
                  width={1200}
                  height={500}
                  priority={true}
                  quality="100"
                />
              }
              {
                race.gc_result ?
                  <section className={s.stage} key={race.id + '_general_classification'}>
                    <header>General classification</header>
                    <div className={s.collapsible}>
                      <div>
                        {
                          race.gc_result.slice(0, 3).map((entry, index: number) => {
                            if (entry.rnk == 1) {
                              entry.medalIco = '🥇';
                            } else if (entry.rnk == 2) {
                              entry.medalIco = '🥈'
                            } else {
                              entry.medalIco = '🥉'
                            }
                            return (
                              <div className={s.entryWinners} key={'general_classification_top_3_' + entry.rider}>
                                <span>
                                  {entry.medalIco}
                                </span>
                                <h4>{entry.rider}</h4>
                                <p>{entry.team}</p>
                              </div>
                            )
                          })
                        }
                      </div>
                      <div className={s.mdGrid2}>
                        {
                          /* @ts-ignore */
                          race.gc_result.slice(3).map((entry, index: number) => {
                            return (
                              <div key={index + 3 + '_stage_results_general_classification'} className={s.entryTeam}>
                                <span>{entry.rnk}.</span>
                                <h4>{entry.rider}</h4>
                                <p>{entry.team}</p>
                              </div>
                            )
                          })
                        }
                      </div>
                    </div>
                  </section> : null
              }
              {
                race.stages.map((stage, index: number) => {
                  return (
                    <section key={index + '_stage_' + stage.stagename} className={s.stage}>
                      <header>{stage.stagename}</header>
                      <div className={s.collapsible}>
                        <div>
                          {
                            stage.results ?
                              stage.results.slice(0, 3).map((entry, index: number) => {
                                if (entry.rnk == 1) {
                                  entry.medalIco = '🥇';
                                } else if (entry.rnk == 2) {
                                  entry.medalIco = '🥈'
                                } else {
                                  entry.medalIco = '🥉'
                                }
                                return (
                                  <div key={index + '_stage_result_top_3'} className={s.entryWinners}>
                                    <span>
                                      {entry.medalIco}
                                    </span>
                                    <h4>{entry.rider}</h4>
                                    <p>{entry.team}</p>
                                  </div>
                                )
                              }) : null
                          }
                        </div>
                        <div className={s.mdGrid2}>
                          {
                            stage.results ?
                              stage.results.slice(3).map((entry, index: number) => {
                                return (
                                  <div key={index + 3 + '_stage_results_' + stage.id} className={s.entryTeam}>
                                    <span>{entry.rnk}.</span>
                                    <h4>{entry.rider}</h4>
                                    <p>{entry.team}</p>
                                  </div>
                                )
                              }) : null
                          }
                        </div>
                      </div>
                    </section>
                  )
                })
              }
            </main>
            <aside>
              <h4 className={s.headline}>Upcoming races</h4>
              <div>
                {
                  upcomingRaces ? upcomingRaces.map((race, index: number) => {
                    return (

                        <div key={index + '_upcoming_races'} className={race.finished ? s.entryActive : s.entry}>
                          <div>
                            <span className={s.entryFlag}>{race.flag}</span>
                            <h4 className="">{race.name}</h4>
                            <p className={s.entryDate}>
                                <span className={race.date_start == race.date_end ? 'hidden' : 'mr-1'}>
                                {
                                  new Date(race.date_start).toLocaleDateString('de-de',
                                    {day: "numeric"})
                                } -
                                </span>
                              {
                                new Date(race.date_end).toLocaleDateString('de-de',
                                  {day: "numeric", month: "long", year: "numeric"})
                              }
                            </p>
                          </div>
                          <Link legacyBehavior href={`./races/${race.final_gc_id}`}>
                            <a className={s.entryButton}><span>More</span></a>
                          </Link>
                          <span className={s.entryChevron}>&rsaquo;</span>
                        </div>

                    )
                  }) : null
                }
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  )
}

Race.Layout = Layout

